/**
 * A wrapper around the EventTarget browser API that resembles the Node.js EventEmitter API
 */
export class EventEmitter extends EventTarget {
  /**
   * Adds a listner function for the specified `eventName`
   * @param {string} eventName The name of the event.
   * @param {(customEvent: CustomEvent) => void} cb The callback function
   * @param {Object} options
   * @param {AbortSignal?} options.signal An optional signal to remove the event listener without calling `off()`
   * @returns The EventEmitter
   */
  on<TData = any>(
    eventName: string,
    cb: (customEvent: CustomEvent<TData>) => void,
    { signal }: { signal?: AbortSignal } = {},
  ) {
    // @ts-expect-error TypeScript changes variance here but this is fine
    this.addEventListener(eventName, cb);

    if (signal) {
      const abortHandler = () => {
        this.off(eventName, cb);
        signal.removeEventListener('abort', abortHandler);
      };
      signal.addEventListener('abort', abortHandler);
    }

    return this;
  }

  /**
   * Adds a one-time listner function for the specified `eventName`
   * @param {string} eventName The name of the event.
   * @param {(customEvent: CustomEvent) => void} cb The callback function
   * @param {Object} options
   * @param {AbortSignal?} options.signal An optional signal to remove the event listener without calling `off()`
   * @returns The EventEmitter
   */
  once<TData = any>(
    eventName: string,
    cb: (customEvent: CustomEvent<TData>) => void,
    { signal }: { signal?: AbortSignal } = {},
  ) {
    const handler = (event: Event) => {
      // @ts-expect-error TypeScript changes variance here but this is fine
      cb(event);
      this.off(eventName, handler);
    };

    this.addEventListener(eventName, handler);

    if (signal) {
      const abortHandler = () => {
        this.off(eventName, handler);
        signal.removeEventListener('abort', abortHandler);
      };
      signal.addEventListener('abort', abortHandler);
    }

    return this;
  }

  /**
   * Removes the specified listners using referential equality for the specified `eventName`
   * @param {string} eventName
   * @param {(customEvent: CustomEvent) => void} cb The callback function
   * @returns The EventEmitter
   */
  off(eventName: string, cb: (customEvent: CustomEvent<any>) => void) {
    // @ts-expect-error TypeScript changes variance here but this is fine
    this.removeEventListener(eventName, cb);

    return this;
  }

  /**
   * Call each listners for the specified event
   * @param {string} eventName Event to call listeners for
   * @param {any} data Data passed throught the `detail` property of the CustomEvent
   * @returns The EventEmitter
   */
  emit(eventName: string, data?: any) {
    const event = new CustomEvent(eventName, { detail: data });

    return this.dispatchEvent(event);
  }
}
